// API Configuration for Court Kiosk Application
// This file centralizes API configuration for both development and production environments

// Get the API base URL from environment variables
// In production (Vercel), this should point to the Render backend
// In development, this can be localhost or any other backend URL
const getApiBaseUrl = () => {
  // 1. Explicit override via environment variable (preferred)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // 2. If we're running in the browser, use window.location to
  //    determine the correct origin. This ensures that when the
  //    frontend is deployed (e.g. on Vercel) we automatically use
  //    the Render backend for API routes.
  if (typeof window !== 'undefined' && window.location) {
    const { hostname } = window.location;

    // When developing locally, the frontend runs on port 3000 but the
    // Flask backend listens on 5001. Detect localhost and point to the
    // backend directly so that `/api/...` requests succeed without any
    // additional configuration.
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `http://localhost:${process.env.REACT_APP_BACKEND_PORT || '5001'}`;
    }

    // In production (Vercel), always use the Render backend
    // This ensures API calls go to the correct backend server
    return 'https://court-kiosk.onrender.com';
  }

  // 3. During SSR or build time on Vercel, use Render backend
  if (process.env.VERCEL_URL) {
    return 'https://court-kiosk.onrender.com';
  }

  // 4. Absolute fallback for other environments.
  return 'https://court-kiosk.onrender.com';
};

// API Configuration
export const API_CONFIG = {
  get BASE_URL() {
    // Dynamically evaluate base URL each time it's accessed
    // This ensures window.location is available when accessed
    return getApiBaseUrl();
  },
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
};

// Shared secret for kiosk-facing write/LLM endpoints (must match backend KIOSK_API_KEY)
export const getKioskApiKey = () => process.env.REACT_APP_KIOSK_API_KEY || '';

/**
 * Default headers for API calls. Includes X-Kiosk-Key when configured.
 * Pass additional headers via `extra`; Authorization should be supplied by callers when needed.
 */
export const getApiHeaders = (extra = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...extra,
  };
  const kioskKey = getKioskApiKey();
  if (kioskKey) {
    headers['X-Kiosk-Key'] = kioskKey;
  }
  return headers;
};

// API Endpoints
export const API_ENDPOINTS = {
  // Queue Management
  QUEUE: '/api/queue',
  GENERATE_QUEUE: '/api/generate-queue',
  CALL_NEXT: '/api/call-next',
  COMPLETE_CASE: '/api/complete-case',
  
  // Case Management
  CASE_SUMMARY: '/api/generate-case-summary',
  SEND_EMAIL: '/api/send-comprehensive-email',
  SEND_CASE_SUMMARY_EMAIL: '/api/email/send-case-summary',
  
  // SMS Service
  SEND_SMS: '/api/sms/send-queue-number',
  
  // Facilitator Management
  FACILITATORS: '/api/facilitators',
  FACILITATOR_CASES: '/api/facilitator/cases',
  ASSIGN_CASE: '/api/queue',
  
  // Health Check
  HEALTH: '/api/health',

  // Family court RAG
  FAMILY_COURT_RAG: '/api/family-court-rag',
  ASK: '/api/ask',
};

// Helper function to build full API URLs
export const buildApiUrl = (endpoint) => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

// Helper function to check if we're in production
export const isProduction = () => {
  return process.env.NODE_ENV === 'production' || process.env.VERCEL === '1';
};

// Helper function to check if we're in development
export const isDevelopment = () => {
  return process.env.NODE_ENV === 'development' && !process.env.VERCEL;
};
